/*  ========================================================================
    # Sections - Icon Cards Slider
    ========================================================================  */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';
import useLocalization from '../../hooks/useLocalization';
import bem from '../../helpers/bem';

const IconCardsSlider = ({
  heading,
  cards,
  showBackground,
  showTransitionTop,
  showTransitionBottom
}) => {
  const __ = useTranslation();
  const { xlUp, mdUp } = useBreakpoints();
  const [activeSlide, setActiveSlide] = useState(0);

  // Icons
  const local = useLocalization();
  const iconChevronLeft = `${local('svgSprite')}#chevron-left`;
  const iconChevronRight = `${local('svgSprite')}#chevron-right`;

  // Set the visible slides corresponding to the current breakpoint.
  const visibleSlides = useMemo(() => {
    if (xlUp) return 3;
    if (mdUp) return 2;
    return 1;
  });

  // Handle navigate right
  const canNavigateRight = activeSlide < cards.length - visibleSlides;
  const handleNavigateRight = () => {
    if (canNavigateRight) {
      setActiveSlide(activeSlide + 1);
    }
  };
  // Handle navigate left
  const canNavigateLeft = activeSlide > 0;
  const handleNavigateLeft = () => {
    if (canNavigateLeft) {
      setActiveSlide(activeSlide - 1);
    }
  };

  // Add swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedRight: handleNavigateLeft,
    onSwipedLeft: handleNavigateRight
  });

  return (
    <>
      {showBackground && showTransitionTop && (
        <div className={bem('u-background', 'divider', { top: true })} />
      )}
      <div className={showBackground ? 'u-background' : ''}>
        <div className={bem('s-icon-cards-slider', 'wrapper')}>
          <div className='container'>
            <div className={bem('s-icon-cards-slider', 'header')}>
              {heading && <h2>{heading}</h2>}
              <div className={bem('s-icon-cards-slider', 'controls')}>
                <button
                  className={bem('c-button', '', { black: true, icon: true })}
                  title={__('slider-control-prev')}
                  aria-label={__('slider-control-prev')}
                  disabled={!canNavigateLeft}
                  onClick={handleNavigateLeft}
                >
                  <svg viewBox='0 0 200 200'>
                    <use href={iconChevronLeft} />
                  </svg>
                </button>
                <button
                  className={bem('c-button', '', { black: true, icon: true })}
                  title={__('slider-control-next')}
                  aria-label={__('slider-control-next')}
                  disabled={!canNavigateRight}
                  onClick={handleNavigateRight}
                >
                  <svg viewBox='0 0 200 200'>
                    <use href={iconChevronRight} />
                  </svg>
                </button>
              </div>
            </div>
            <div
              className={bem('s-icon-cards-slider', 'slider')}
              style={{ '--active-slide': activeSlide, '--visible-slides': visibleSlides }}
              {...swipeHandlers}
            >
              {cards.map((card, index) => (
                <div className={`${bem('s-icon-cards-slider', 'slide')}`} key={index}>
                  <div className={`${bem('s-icon-cards-slider', 'slide-wrapper')}`}>
                    <figure className={bem('s-icon-cards-slider', 'slide-icon')}>
                      <img src={card.icon.sizes.medium} alt={card.icon.alt} />
                    </figure>
                    <div
                      className={`${bem('s-icon-cards-slider', 'slide-content')} wysiwyg-block`}
                      dangerouslySetInnerHTML={{ __html: card.description }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {showBackground && showTransitionBottom && (
        <div className={bem('u-background', 'divider', { bottom: true })} />
      )}
    </>
  );
};

IconCardsSlider.propTypes = {
  showBackground: PropTypes.bool,
  showTransitionTop: PropTypes.bool,
  showTransitionBottom: PropTypes.bool,
  heading: PropTypes.string,
  cards: PropTypes.array
};

export default IconCardsSlider;
